import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AuthService } from 'src/app/auth.service';

// Quill.sources values (quill/core/emitter.js) — inlined rather than importing the `quill` package
// directly, which would pull Quill's ~427KB core out of ngx-quill's lazy chunk and into this
// always-loaded component's eager bundle.
const QUILL_SOURCE_USER = 'user';
const QUILL_SOURCE_SILENT = 'silent';

// Teal, palette column 11 (teal family) / bottom row (full bold) — see the color-name
// map below for the full palette. Marks content specifically identifying an author
// (an Analyst's inserted name/username, or the "Analysis by DTR Copilot" label the
// server generates) — distinct from the plain light-blue timestamp accent.
const AUTHOR_IDENTITY_COLOR = '#00ffea';

// Human-readable names for the Font Color picker's 13-column x 4-row palette (see
// transformer-description.component.html for the swatch values themselves) — Quill's
// Picker (quill/ui/picker.js buildItem()) does not carry a source <option>'s `title`
// attribute over to the rendered swatch at all, so a hover tooltip has to be applied
// directly to the rendered `.ql-picker-item` elements after Quill builds them (see
// applyColorSwatchTooltips(), called from onEditorCreated below). Keyed by lowercase
// hex so lookup is case-insensitive.
const COLOR_SWATCH_NAMES: { [hex: string]: string } = {
  // Row 1 — light pastel
  '#ffffff': 'White',
  '#eeebd3': 'Light Pastel Yellow',
  '#eee4d3': 'Light Pastel Amber',
  '#eeded3': 'Light Pastel Orange',
  '#eed3d3': 'Light Pastel Red',
  '#eed3e0': 'Light Pastel Pink',
  '#ead3ee': 'Light Pastel Purple',
  '#dad3ee': 'Light Pastel Indigo',
  '#d3e0ee': 'Light Pastel Blue',
  '#d3e7ee': 'Light Pastel Sky',
  '#d3eeec': 'Light Pastel Teal',
  '#d3eed5': 'Light Pastel Green',
  '#e9eed3': 'Light Pastel Yellow-Green',
  // Row 2 — medium pastel
  '#e0e0e0': 'Light Grey',
  '#e9e2a5': 'Medium Pastel Yellow',
  '#e9d0a5': 'Medium Pastel Amber',
  '#e9c1a5': 'Medium Pastel Orange',
  '#e9a5a5': 'Medium Pastel Red',
  '#e9a5c7': 'Medium Pastel Pink',
  '#dda5e9': 'Medium Pastel Purple',
  '#b6a5e9': 'Medium Pastel Indigo',
  '#a5c7e9': 'Medium Pastel Blue',
  '#a5d8e9': 'Medium Pastel Sky',
  '#a5e9e3': 'Medium Pastel Teal',
  '#a5e9ab': 'Medium Pastel Green',
  '#dbe9a5': 'Medium Pastel Yellow-Green',
  // Row 3 — semi-bold
  '#b8b8b8': 'Grey',
  '#e8de87': 'Semi-Bold Yellow',
  '#e8c587': 'Semi-Bold Amber',
  '#e8b087': 'Semi-Bold Orange',
  '#e88787': 'Semi-Bold Red',
  '#e887b8': 'Semi-Bold Pink',
  '#d887e8': 'Semi-Bold Purple',
  '#9f87e8': 'Semi-Bold Indigo',
  '#87b8e8': 'Semi-Bold Blue',
  '#87d0e8': 'Semi-Bold Sky',
  '#87e8e0': 'Semi-Bold Teal',
  '#87e88f': 'Semi-Bold Green',
  '#d5e887': 'Semi-Bold Yellow-Green',
  // Row 4 — full bold (pure, fully-saturated hue)
  '#757575': 'Dark Grey',
  '#ffe500': 'Full Bold Yellow',
  '#ffa200': 'Full Bold Amber',
  '#ff6a00': 'Full Bold Orange',
  '#ff0000': 'Full Bold Red',
  '#ff0080': 'Full Bold Pink',
  '#d400ff': 'Full Bold Purple',
  '#4000ff': 'Full Bold Indigo',
  '#007fff': 'Full Bold Blue',
  '#00bfff': 'Full Bold Sky',
  '#00ffea': 'Full Bold Teal',
  '#00ff15': 'Full Bold Green',
  '#ccff00': 'Full Bold Yellow-Green',
};

// Quill's built-in "list autofill" binding only recognizes "1. " (digits + period) as an ordered-list
// starter. This widens that to also accept "1) " (digits + closing paren), keeping every other part of
// Quill's own handler (quill/modules/keyboard.js) unchanged. `Delta` is pulled off the already-loaded
// Quill instance at runtime (`this.quill.constructor.import('delta')`) for the same lazy-loading reason.
const listAutofillWithParenSupport = {
  key: ' ',
  shiftKey: null,
  collapsed: true,
  format: { 'code-block': false, blockquote: false, table: false },
  prefix: /^\s*?(\d+\.|\d+\)|-|\*|\[ ?\]|\[x\])$/,
  handler(this: any, range: any, context: any): boolean {
    if (this.quill.scroll.query('list') == null) {
      return true;
    }
    const { length } = context.prefix;
    const [line, offset] = this.quill.getLine(range.index);
    if (offset > length) {
      return true;
    }
    let value: string;
    switch (context.prefix.trim()) {
      case '[]':
      case '[ ]':
        value = 'unchecked';
        break;
      case '[x]':
        value = 'checked';
        break;
      case '-':
      case '*':
        value = 'bullet';
        break;
      default:
        value = 'ordered';
    }
    const Delta = this.quill.constructor.import('delta');
    this.quill.insertText(range.index, ' ', QUILL_SOURCE_USER);
    this.quill.history.cutoff();
    const delta = new Delta().retain(range.index - offset).delete(length + 1)
      .retain(line.length() - 2 - offset).retain(1, { list: value });
    this.quill.updateContents(delta, QUILL_SOURCE_USER);
    this.quill.history.cutoff();
    this.quill.setSelection(range.index - length, QUILL_SOURCE_SILENT);
    return false;
  }
};

let dividerRegistered = false;

// Registers a custom "divider" block-embed format (renders as <hr>) the first time an editor is about
// to render. Done inside beforeRender — which only fires once ngx-quill has already lazily loaded the
// `quill` package for its own editor instance — via a dynamic import() so this stays out of the eager
// bundle, same reasoning as the inlined Quill.sources constants above.
async function registerDividerFormat(): Promise<void> {
  if (dividerRegistered) {
    return;
  }
  dividerRegistered = true;
  const [{ default: Quill }, { BlockEmbed }] = await Promise.all([
    import('quill'),
    import('quill/blots/block')
  ]);
  class DividerBlot extends BlockEmbed {
    static blotName = 'divider';
    static tagName = 'hr';
  }
  Quill.register('formats/divider', DividerBlot, true);
}

// "---" on its own line, followed by Enter, becomes a full-width divider.
const dividerAutofill = {
  key: 'Enter',
  collapsed: true,
  format: { 'code-block': false, blockquote: false, table: false },
  prefix: /^---$/,
  handler(this: any, range: any, context: any): boolean {
    const { length } = context.prefix;
    const startIndex = range.index - length;
    this.quill.deleteText(startIndex, length, QUILL_SOURCE_USER);
    this.quill.insertEmbed(startIndex, 'divider', true, QUILL_SOURCE_USER);
    this.quill.setSelection(startIndex + 1, QUILL_SOURCE_SILENT);
    return false;
  }
};

@Component({
    selector: 'app-transformer-description',
    templateUrl: './transformer-description.component.html',
    styleUrls: ['./transformer-description.component.scss'],
    standalone: false
})
export class TransformerDescriptionComponent implements OnChanges {
  @Input() analystDescription: string;
  @Output() analystDescriptionChange = new EventEmitter<string>();

  content = '';
  isExpanded = false;
  private lastEmitted: string = null;
  private quill: any = null;

  constructor(private authService: AuthService) {}

  quillModules = {
    keyboard: {
      bindings: {
        'list autofill': listAutofillWithParenSupport,
        'divider autofill': dividerAutofill
      }
    }
  };

  beforeRender = () => registerDividerFormat();


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['analystDescription']) {
      const incoming = this.analystDescription ?? '';
      // Skip syncing our own change echoing back down through the parent's binding —
      // re-feeding Quill's [ngModel] on every keystroke resets the editor and throws the cursor to position 0.
      if (incoming === this.lastEmitted) {
        return;
      }
      this.content = incoming;
    }
  }

  onContentChange(html: string): void {
    // Not run through DomSanitizer: Angular's HTML sanitizer strips every `style` attribute
    // unconditionally (confirmed against its own VALID_ATTRS allowlist — style isn't in it at
    // all), which silently destroyed font color on every save. `html` here is Quill's own
    // getSemanticHTML() output, constrained to only the formats registered on this editor
    // (bold/italic/underline/color/size/link/lists/code-block/divider) — Quill's Delta model
    // can't represent a <script> tag or an event-handler attribute, and its link format already
    // rejects dangerous URL schemes, so this is safe by construction without Angular's sanitizer.
    this.lastEmitted = html;
    this.analystDescription = html;
    this.analystDescriptionChange.emit(html);
  }

  onEditorCreated(quill: any): void {
    this.quill = quill;
    this.applyColorSwatchTooltips(quill);
  }

  // Hover tooltip naming each Font Color swatch — Quill's Picker doesn't carry a
  // source <option>'s `title` over to the rendered item (see COLOR_SWATCH_NAMES
  // above), so it's applied directly to the built DOM once Quill has rendered it.
  private applyColorSwatchTooltips(quill: any): void {
    const root: HTMLElement | null = quill?.container?.parentElement ?? null;
    if (!root) {
      return;
    }
    root.querySelectorAll('.ql-color-picker .ql-picker-item').forEach((item: Element) => {
      const hex = item.getAttribute('data-value')?.toLowerCase();
      const name = hex ? COLOR_SWATCH_NAMES[hex] : undefined;
      if (name) {
        item.setAttribute('title', name);
      }
    });
  }

  insertTimestamp(): void {
    if (!this.quill) {
      return;
    }
    const range = this.quill.getSelection(true) ?? { index: this.quill.getLength(), length: 0 };
    const timestamp = formatTimestamp(new Date());
    const user = this.authService.getUserDetails();
    // Light blue for the timestamp; teal (AUTHOR_IDENTITY_COLOR) for the name/username
    // identity — two separate colored runs, not one uniform color, per the Architect's
    // follow-up request. <User Name> is the person's display name (e.g. "Scott
    // Leslie"), (<userId>) is their login username (e.g. "sdleslie") — NOT the numeric
    // database id, despite the field on this object confusingly being named userName.
    this.quill.insertText(range.index, timestamp, { color: '#42a5f5' }, QUILL_SOURCE_USER);
    let cursor = range.index + timestamp.length;
    if (user) {
      const identity = ` ${user.firstName} ${user.lastName} (${user.userName})`;
      this.quill.insertText(cursor, identity, { color: AUTHOR_IDENTITY_COLOR }, QUILL_SOURCE_USER);
      cursor += identity.length;
    }
    this.quill.setSelection(cursor, QUILL_SOURCE_SILENT);
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  undo(): void {
    this.quill?.history.undo();
  }

  redo(): void {
    this.quill?.history.redo();
  }
}

function formatTimestamp(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
